/**
 * F-177: the member page at `/_/members/{id}` (and `/_/members/{id}/achievements`,
 * the F-148 e-mail link, which opens it on Conquistas). Pure helpers, shared by
 * the page, the preview dialog and the tests.
 */

export const MEMBER_PAGE_PREFIX = '/_/members/'

export type MemberPageTab = 'overview' | 'activity' | 'achievements' | 'credits'
export const MEMBER_PAGE_TABS: MemberPageTab[] = ['overview', 'activity', 'achievements', 'credits']

const UUID = '[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}'
/* Only a uuid: `/_/members/pending` is another route (F-150). */
const PAGE_RE = new RegExp(`^/_/members/(${UUID})(?:/(achievements|activity|credits))?/?$`, 'i')

/** The page's link; `tab` other than the overview goes as a path segment (achievements) or `?tab=`. */
export const memberPagePath = (memberId: string, tab?: MemberPageTab | string | null): string => {
  const base = `${MEMBER_PAGE_PREFIX}${encodeURIComponent(memberId)}`
  if (!tab || tab === 'overview') return base
  return `${base}/${tab}`
}

/** `{ memberId, tab }` of a member page path (query and hash aside), or null for any other path. */
export const parseMemberPagePath = (path: string): { memberId: string; tab: MemberPageTab | null } | null => {
  const m = path.split(/[?#]/)[0].match(PAGE_RE)
  if (!m) return null
  return { memberId: m[1].toLowerCase(), tab: (m[2]?.toLowerCase() as MemberPageTab | undefined) ?? null }
}

export const isMemberPagePath = (path: string): boolean => parseMemberPagePath(path) !== null

/** The flag emoji of an ISO 3166-1 alpha-2 code ("BR" → 🇧🇷); empty for anything else. */
export const countryFlag = (code?: string | null): string => {
  if (!code || !/^[A-Za-z]{2}$/.test(code)) return ''
  const base = 0x1f1e6
  return String.fromCodePoint(...code.toUpperCase().split('').map((c) => base + c.charCodeAt(0) - 65))
}

const displayName = (locale: string, type: 'region' | 'language', code: string): string => {
  try {
    return new Intl.DisplayNames([locale], { type, fallback: 'code' }).of(code) || code
  } catch {
    return code
  }
}
export const countryName = (code: string, locale: string): string => displayName(locale, 'region', code.toUpperCase())
export const languageName = (code: string, locale: string): string => {
  const name = displayName(locale, 'language', code)
  return name.charAt(0).toLocaleUpperCase(locale) + name.slice(1)
}

/** Every ISO country the browser can name, sorted by name in `locale`. */
export const countryOptions = (locale: string): { code: string; name: string }[] => {
  const out: { code: string; name: string }[] = []
  let names: Intl.DisplayNames | null = null
  try { names = new Intl.DisplayNames([locale], { type: 'region', fallback: 'none' }) } catch { return out }
  for (let a = 65; a <= 90; a++) {
    for (let b = 65; b <= 90; b++) {
      const code = String.fromCharCode(a, b)
      if (['ZZ', 'EU', 'UN', 'EZ', 'QO', 'XA', 'XB', 'AA'].includes(code)) continue
      let name: string | undefined
      try { name = names.of(code) } catch { name = undefined }
      if (name && name !== code) out.push({ code, name })
    }
  }
  return out.sort((x, y) => x.name.localeCompare(y.name, locale))
}

/** The languages offered in Editar perfil (ISO 639-1); the API takes any valid code. */
export const LANGUAGE_CODES = [
  'pt', 'en', 'es', 'fr', 'de', 'it', 'nl', 'pl', 'ru', 'uk', 'tr', 'ar', 'he', 'hi', 'bn', 'ur',
  'zh', 'ja', 'ko', 'vi', 'th', 'id', 'ms', 'tl', 'sv', 'no', 'da', 'fi', 'el', 'cs', 'ro', 'hu', 'ca', 'gl', 'eu',
]

/** `YYYY-MM-DD` or `YYYY-MM` → `YYYY-MM` (the `<input type="month">` value). */
export const monthOf = (date?: string | null): string => (date ? date.slice(0, 7) : '')

/** Experiences newest first: current ones on top, then by start. */
export const sortExperiences = <T extends { startDate: string; endDate: string | null }>(list: T[]): T[] =>
  [...list].sort((a, b) => {
    if (!a.endDate !== !b.endDate) return a.endDate ? 1 : -1
    return (b.startDate || '').localeCompare(a.startDate || '')
  })

/** Which tabs a page shows: never an empty one (criterion 3). */
export const visiblePageTabs = (ctx: {
  activityVisible: boolean
  activityCount: number
  achievementsCount: number
  canAwardBadges: boolean
  isOwnProfile: boolean
  isTeam: boolean
  signedOut: boolean
}): MemberPageTab[] => {
  const tabs: MemberPageTab[] = ['overview']
  if (ctx.activityVisible && ctx.activityCount > 0) tabs.push('activity')
  if (ctx.achievementsCount > 0 || (ctx.canAwardBadges && !ctx.signedOut)) tabs.push('achievements')
  if (!ctx.signedOut && (ctx.isOwnProfile || ctx.isTeam)) tabs.push('credits')
  return tabs
}
